import type {
  DevicePresence,
  DisplayCommand,
  GoonSession,
  MediaItem,
  RoomActivity,
  RoomChatMessage,
  RoomMember,
  RoomModAction,
  RoomPage,
  RoomRefusal,
  RoomRegionState,
  RoomRole,
  RoomRtcIce,
  RoomRtcSdp,
  RoomStageState,
  View,
  WsMessage,
} from "@gooner/shared";
import { getAnonId, getUserToken } from "./api";
import { track } from "../analytics";

function wsUrl(): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}/ws`;
}

interface DisplayHandlers {
  /** Initial config sent by the server right after `hello`. */
  onConfig: (view: View) => void;
  /** Live edit pushed from a controller. */
  onUpdate: (view: View) => void;
  /** Remote-control command addressed to this device. */
  onCommand: (cmd: DisplayCommand) => void;
  /** The operator's live goon session (or null when none is running). */
  onSession?: (session: GoonSession | null) => void;
  onStatus?: (connected: boolean) => void;
}

export interface DisplayConn {
  /** One heartbeat: the ping frame the server's presence, reaper and temp-view keepalive
   *  all read. Carries the last measured round-trip and the send time; the `pong` that
   *  comes back is what measures the next one. */
  beat: () => void;
  /**
   * Report whether this screen is muted. Rides the same heartbeat message as latency —
   * sent at once when it changes (so the remote's toggle reads true within a round trip)
   * and again with every beat after, so a remote that connects later learns it too.
   */
  reportMuted: (muted: boolean) => void;
  close: () => void;
}

/**
 * Connect a screen. Auto-reconnects.
 *
 * `deviceId` is what makes this screen a device: with it the socket joins its owner's
 * room (so it receives the session) and becomes addressable by `command`. Without one it
 * is an anonymous viewer of a view, which is all a preview needs.
 *
 * `viewId` may be NULL — a paired screen that has not been given anything yet. It still
 * connects, because being connected is how it reports that it is switched on, and that is
 * what lets a remote see it and hand it a view. Requiring one here was half of a deadlock:
 * the screen needed a view to appear online, and the only control that could give it one
 * was disabled until it appeared online.
 */
export function connectDisplay(
  viewId: string | null,
  h: DisplayHandlers,
  opts: { deviceId?: string } = {},
): DisplayConn {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  // The last latency measured and the last mute state reported, so either half of the
  // heartbeat can be re-sent with the other's current value.
  let lastMs = 0;
  let lastMuted: boolean | undefined;

  const send = (m: WsMessage) => {
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
  };

  const open = () => {
    ws = new WebSocket(wsUrl());
    ws.onopen = () => {
      h.onStatus?.(true);
      // OMITTED when null, not sent as null. The server tests `typeof msg.viewId ===
      // "string"`, so a literal null would take the right path only by coincidence, and
      // the wire would carry a field that means "no field".
      send({
        t: "hello",
        role: "display",
        ...(viewId ? { viewId } : {}),
        deviceId: opts.deviceId,
      });
    };
    ws.onmessage = (e) => {
      let msg: WsMessage;
      try {
        msg = JSON.parse(e.data) as WsMessage;
      } catch {
        return;
      }
      if (msg.t === "config") h.onConfig(msg.view);
      else if (msg.t === "update" && msg.view.id === viewId) h.onUpdate(msg.view);
      else if (msg.t === "command" && msg.deviceId === opts.deviceId) h.onCommand(msg.cmd);
      else if (msg.t === "session") h.onSession?.(msg.session);
      // Latency is the echo's age. It rides the NEXT beat rather than a message of its
      // own — a number for the remote's rail, not a measurement anyone waits on.
      else if (msg.t === "pong") lastMs = Math.max(0, Date.now() - msg.sent);
    };
    ws.onclose = (e) => {
      // Only an UNINTENDED close reports disconnect / retries. `closed` means the caller
      // asked for it — a mode switch tears the socket down and opens another for the new
      // viewId (DisplayApp). Firing onStatus(false) here races the replacement's onopen:
      // a late close after the new socket is already up leaves the glass stuck on
      // "Reconnecting…" while the live socket is fine (2026-10-07 QA: Solo→Build and
      // rapid mode switches). A screen navigating away likewise owes no badge.
      //
      // Rate-limited to 5/min in track(): reconnection retries every 1.5s, so a server
      // that is simply down produces 40 closes a minute per screen, indefinitely.
      if (!closed) {
        h.onStatus?.(false);
        track("ws_disconnected", { code: e.code, clean: e.wasClean, role: "display" });
        retry = setTimeout(open, 1500);
      }
    };
    ws.onerror = () => ws?.close();
  };

  open();
  return {
    beat: () => send({ t: "ping", ms: lastMs, muted: lastMuted, sent: Date.now() }),
    reportMuted: (muted) => {
      lastMuted = muted;
      send({ t: "ping", ms: lastMs, muted, sent: Date.now() });
    },
    close() {
      closed = true;
      if (retry) clearTimeout(retry);
      ws?.close();
    },
  };
}

export interface ControlConn {
  /** Push a live view edit to every screen showing that view. */
  pushUpdate: (view: View) => void;
  /** Send a remote-control command to one screen. */
  sendCommand: (deviceId: string, cmd: DisplayCommand) => void;
  close: () => void;
}

interface ControlHandlers {
  onStatus?: (connected: boolean) => void;
  /** Live presence per device for this operator. */
  onPresence?: (devices: Record<string, DevicePresence>) => void;
  /**
   * This operator's goon session, whenever it changes anywhere.
   *
   * A remote used to fetch the session once on mount and then only learn about the edges
   * it added itself — so a fap counted on the wall, or an edge added on a second remote,
   * never reached it. The screens have always been sent this; the controls were the half
   * that was missing.
   */
  onSession?: (session: GoonSession | null) => void;
}

/**
 * Connect a controller. Messages sent before the socket opens are queued.
 *
 * The hello carries the same identity material every owner-scoped REST call sends, and
 * the server resolves it the same way — the socket never states an owner id, because a
 * stated one would make the ownership check on `update` worthless.
 */
export function connectControl(handlers: ControlHandlers = {}): ControlConn {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let queue: WsMessage[] = [];

  const raw = (m: WsMessage) => {
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
    else queue.push(m);
  };

  const open = () => {
    ws = new WebSocket(wsUrl());
    ws.onopen = () => {
      handlers.onStatus?.(true);
      raw({ t: "hello", role: "control", token: getUserToken() ?? undefined, anonId: getAnonId() });
      const pending = queue;
      queue = [];
      for (const m of pending) raw(m);
    };
    ws.onmessage = (e) => {
      let msg: WsMessage;
      try {
        msg = JSON.parse(e.data) as WsMessage;
      } catch {
        return;
      }
      if (msg.t === "presence") handlers.onPresence?.(msg.devices);
      else if (msg.t === "session") handlers.onSession?.(msg.session);
    };
    ws.onclose = (e) => {
      handlers.onStatus?.(false);
      // Same reasoning as the display connection above: intended closes are not news.
      // `role` separates the two, because a control losing the socket and a screen losing
      // it are different failures — one is an operator who cannot drive, the other is a
      // wall that has stopped taking instruction.
      if (!closed) {
        track("ws_disconnected", { code: e.code, clean: e.wasClean, role: "control" });
        retry = setTimeout(open, 1500);
      }
    };
    ws.onerror = () => ws?.close();
  };

  open();
  return {
    pushUpdate: (view) => raw({ t: "update", view }),
    sendCommand: (deviceId, cmd) => raw({ t: "command", deviceId, cmd }),
    close() {
      closed = true;
      if (retry) clearTimeout(retry);
      ws?.close();
    },
  };
}

export interface RoomHandlers {
  onStatus?: (connected: boolean) => void;
  /** The snapshot on (re)join: who you are in it, who else is, the tail of the chat. */
  onState: (s: {
    name: string;
    public: boolean;
    you: string;
    role: RoomRole;
    members: RoomMember[];
    /** Everyone watching: the faces plus the signed-out viewers, who have none. */
    watching: number;
    messages: RoomChatMessage[];
    activity: RoomActivity[];
    paused: boolean;
    house: boolean;
    chatOff: boolean;
    regions: Record<string, RoomRegionState>;
    /** THE MIRROR: what the host is showing (masked for a member) and, on a scroll, the
     *  host's pages and the page on their glass. */
    showing: string | null;
    stage: RoomStageState | null;
  }) => void;
  onRoster: (members: RoomMember[], watching: number) => void;
  /** THE MIRROR: the host re-pointed; a page the host dealt; the page on the host's glass. */
  onShowing?: (viewId: string | null) => void;
  /** A line of what somebody did — the same id again is that line, updated (a count). */
  onActivity?: (activity: RoomActivity) => void;
  onPage?: (page: RoomPage) => void;
  onActive?: (active: number) => void;
  /** THE SYNC: one of the host's regions revealed a clip; the host paused/resumed. */
  onPlaying?: (regionId: string, state: RoomRegionState) => void;
  onPause?: (paused: boolean) => void;
  onMessage: (m: RoomChatMessage) => void;
  onCamOff?: () => void;
  onRtc?: (from: string, sdp?: RoomRtcSdp, ice?: RoomRtcIce) => void;
  onKicked: (reason: "kicked" | "banned" | "ended") => void;
  onFap?: (regionId: string, username?: string) => void;
  onClosed: () => void;
  onRefused: (reason: RoomRefusal, message: string) => void;
}

export interface RoomConn {
  sendChat: (body: string, imageKey?: string) => void;
  setCam: (on: boolean) => void;
  sendRtc: (to: string, payload: { sdp?: RoomRtcSdp; ice?: RoomRtcIce }) => void;
  mod: (action: RoomModAction, username: string) => void;
  sendFap: (regionId: string) => void;
  /** Owner only. NOT queued while the socket is down — a reveal is news for a few
   *  seconds, and replaying a backlog on reconnect would start members on clips the
   *  host's tiles have long moved past. The next reveal comes within a clip. */
  sendReveal: (regionId: string, item: MediaItem, src: string) => void;
  sendPause: (paused: boolean) => void;
  /** What MY screen did that the room cannot see: a goon session started or ended, an edge. */
  sendAct: (kind: "session_start" | "session_end" | "edge", minutes?: number) => void;
  /** Owner only, THE MIRROR — and not queued either: a view, a page, the active page
   *  are STATE, and the host re-sends what it has when the socket comes back rather
   *  than replaying every step it took while the socket was down. */
  sendView: (view: View) => void;
  sendPage: (page: RoomPage) => void;
  sendActive: (active: number) => void;
  close: () => void;
}

/**
 * Join a Goon Room as a person. Messages sent before the socket opens are queued.
 *
 * Reconnects like the others — EXCEPT after a verdict: kicked, banned, the room closed,
 * a refusal at the door (sign in, full, no such room). Those are answers, and a socket
 * that came back every 1.5 s after being thrown out would be a member who will not
 * leave. The hello carries the user token; the server resolves it and stamps every
 * frame it emits itself — the client never states who it is.
 */
export function connectRoom(roomId: string, h: RoomHandlers): RoomConn {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let queue: WsMessage[] = [];

  const raw = (m: WsMessage) => {
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
    else if (!closed) queue.push(m);
  };
  const verdict = () => {
    closed = true;
    if (retry) clearTimeout(retry);
  };

  const open = () => {
    ws = new WebSocket(wsUrl());
    ws.onopen = () => {
      h.onStatus?.(true);
      raw({ t: "hello", role: "room", roomId, token: getUserToken() ?? undefined });
      const pending = queue;
      queue = [];
      for (const m of pending) raw(m);
    };
    ws.onmessage = (e) => {
      let msg: WsMessage;
      try {
        msg = JSON.parse(e.data) as WsMessage;
      } catch {
        return;
      }
      switch (msg.t) {
        case "room_state":
          h.onState({
            name: msg.name,
            public: msg.public,
            you: msg.you,
            role: msg.role,
            members: msg.members,
            watching: msg.watching ?? msg.members.length,
            messages: msg.messages,
            activity: msg.activity ?? [],
            paused: msg.paused === true,
            regions: msg.regions ?? {},
            showing: msg.showing ?? null,
            stage: msg.stage ?? null,
            house: msg.house === true,
            chatOff: msg.chatOff === true,
          });
          return;
        case "room_showing":
          h.onShowing?.(msg.viewId ?? null);
          return;
        case "room_activity":
          h.onActivity?.(msg.activity);
          return;
        case "room_page":
          h.onPage?.(msg.page);
          return;
        case "room_active":
          h.onActive?.(msg.active);
          return;
        case "room_playing":
          h.onPlaying?.(msg.regionId, msg.state);
          return;
        case "room_pause":
          h.onPause?.(msg.paused);
          return;
        case "room_roster":
          h.onRoster(msg.members, msg.watching ?? msg.members.length);
          return;
        case "room_message":
          h.onMessage(msg.message);
          return;
        case "room_cam_off":
          h.onCamOff?.();
          return;
        case "room_rtc":
          if (msg.from) h.onRtc?.(msg.from, msg.sdp, msg.ice);
          return;
        case "room_fap":
          h.onFap?.(msg.regionId, msg.username);
          return;
        case "room_kicked":
          verdict();
          h.onKicked(msg.reason);
          return;
        case "room_closed":
          verdict();
          h.onClosed();
          return;
        case "room_refused":
          // A door refusal is final; a frame refusal (rate, too long) is not.
          if (msg.reason === "signin" || msg.reason === "banned" || msg.reason === "no_room" || msg.reason === "full") verdict();
          h.onRefused(msg.reason, msg.message);
          return;
        default:
          return;
      }
    };
    ws.onclose = (e) => {
      h.onStatus?.(false);
      if (!closed) {
        track("ws_disconnected", { code: e.code, clean: e.wasClean, role: "room" });
        retry = setTimeout(open, 1500);
      }
    };
    ws.onerror = () => ws?.close();
  };

  open();
  return {
    sendChat: (body, imageKey) => raw({ t: "room_chat", ...(body ? { body } : {}), ...(imageKey ? { imageKey } : {}) }),
    setCam: (on) => raw({ t: "room_cam", on }),
    sendRtc: (to, payload) => raw({ t: "room_rtc", to, ...payload }),
    mod: (action, username) => raw({ t: "room_mod", action, username }),
    sendFap: (regionId) => raw({ t: "room_fap", regionId }),
    sendReveal: (regionId, item, src) => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "room_reveal", regionId, item, src }));
    },
    sendPause: (paused) => raw({ t: "room_pause", paused }),
    sendAct: (kind, minutes) => raw({ t: "room_act", kind, ...(minutes !== undefined ? { minutes } : {}) }),
    sendView: (view) => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "room_view", view }));
    },
    sendPage: (page) => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "room_page", page }));
    },
    sendActive: (active) => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "room_active", active }));
    },
    close() {
      closed = true;
      if (retry) clearTimeout(retry);
      ws?.close();
    },
  };
}
